import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Columns3, Search, Check, X, GripVertical } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { Checkbox } from '../../../../components/ui/checkbox';
import { useAnalytics } from '../../../../core/analytics';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';

type ColumnSelectorProps = {
  columns: TableExplorerColumn[];
  selectedColumns: string[] | '*';
  onSelectedColumnsChange: (columns: string[] | '*') => void;
  disabled?: boolean;
};

type RowItemProps = {
  column: TableExplorerColumn;
  index: number;
  isInDraft: boolean;
  isHighlighted: boolean;
  isOnlyVisible: boolean;
  onToggle: (name: string) => void;
  onHover: (index: number) => void;
};

function SortableVisibleRow({
  column,
  index,
  isInDraft,
  isHighlighted,
  isOnlyVisible,
  onToggle,
  onHover,
}: RowItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.name });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  } as const;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-index={index}
      data-testid={`column-item-${column.name}`}
      onMouseEnter={() => onHover(index)}
      className={`flex items-center gap-1.5 px-1 py-1.5 text-xs rounded ${
        isHighlighted ? 'bg-accent' : 'hover:bg-accent'
      } ${isOnlyVisible ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex items-center justify-center w-4 h-4 text-muted-foreground/60 hover:text-foreground cursor-grab active:cursor-grabbing touch-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
        aria-label={`Drag to reorder ${column.name}`}
        data-testid={`column-drag-handle-${column.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <label
        className={`flex flex-1 items-center gap-2 cursor-pointer ${
          isOnlyVisible ? 'cursor-not-allowed' : ''
        }`}
      >
        <Checkbox
          checked={isInDraft}
          onCheckedChange={() => onToggle(column.name)}
          disabled={isOnlyVisible}
          className="h-3.5 w-3.5"
          data-testid={`column-checkbox-${column.name}`}
        />
        <span className="flex-1 font-medium truncate">{column.name}</span>
        <span className="text-muted-foreground text-[10px] shrink-0">
          {column.dataType}
        </span>
      </label>
    </div>
  );
}

function HiddenRow({
  column,
  index,
  isHighlighted,
  onToggle,
  onHover,
}: Omit<RowItemProps, 'isInDraft' | 'isOnlyVisible'>) {
  return (
    <div
      data-index={index}
      data-testid={`column-item-${column.name}`}
      onMouseEnter={() => onHover(index)}
      className={`flex items-center gap-1.5 px-1 py-1.5 text-xs rounded ${
        isHighlighted ? 'bg-accent' : 'hover:bg-accent'
      }`}
    >
      <span className="w-4 h-4" aria-hidden="true" />
      <label className="flex flex-1 items-center gap-2 cursor-pointer">
        <Checkbox
          checked={false}
          onCheckedChange={() => onToggle(column.name)}
          className="h-3.5 w-3.5"
          data-testid={`column-checkbox-${column.name}`}
        />
        <span className="flex-1 font-medium truncate">{column.name}</span>
        <span className="text-muted-foreground text-[10px] shrink-0">
          {column.dataType}
        </span>
      </label>
    </div>
  );
}

export function ColumnSelector({
  columns,
  selectedColumns,
  onSelectedColumnsChange,
  disabled = false,
}: ColumnSelectorProps) {
  const { track } = useAnalytics();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Draft state for selected columns (local state until applied)
  const [draftSelectedColumns, setDraftSelectedColumns] = useState<string[]>([]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Convert current selection to array, in display order
  const currentSelectedArray = useMemo(() => {
    if (selectedColumns === '*') return columns.map((col) => col.name);
    return selectedColumns;
  }, [selectedColumns, columns]);

  // Initialize draft state when popover opens
  useEffect(() => {
    if (isOpen) {
      setDraftSelectedColumns(currentSelectedArray);
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen, currentSelectedArray]);

  // Derived: visible draft list (in display order) and hidden columns (in metadata order)
  const draftSet = useMemo(() => new Set(draftSelectedColumns), [draftSelectedColumns]);

  const visibleDraftColumns = useMemo(() => {
    const byName = new Map(columns.map((col) => [col.name, col]));
    return draftSelectedColumns
      .map((name) => byName.get(name))
      .filter((col): col is TableExplorerColumn => col !== undefined);
  }, [draftSelectedColumns, columns]);

  const hiddenColumns = useMemo(
    () => columns.filter((col) => !draftSet.has(col.name)),
    [columns, draftSet]
  );

  // Apply search filter
  const filteredVisible = useMemo(() => {
    if (!searchQuery.trim()) return visibleDraftColumns;
    const q = searchQuery.toLowerCase();
    return visibleDraftColumns.filter((col) => col.name.toLowerCase().includes(q));
  }, [visibleDraftColumns, searchQuery]);

  const filteredHidden = useMemo(() => {
    if (!searchQuery.trim()) return hiddenColumns;
    const q = searchQuery.toLowerCase();
    return hiddenColumns.filter((col) => col.name.toLowerCase().includes(q));
  }, [hiddenColumns, searchQuery]);

  // Combined navigation order (visible first, then hidden) for keyboard nav
  const navOrderedColumns = useMemo(
    () => [...filteredVisible, ...filteredHidden],
    [filteredVisible, filteredHidden]
  );

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const selectedItem = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      selectedItem?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Detect changes: any difference in length, membership, OR order
  const hasChanges = useMemo(() => {
    if (draftSelectedColumns.length !== currentSelectedArray.length) return true;
    return !draftSelectedColumns.every((col, idx) => col === currentSelectedArray[idx]);
  }, [draftSelectedColumns, currentSelectedArray]);

  // Toggle a single column in draft
  const handleToggleColumn = useCallback(
    (columnName: string) => {
      setDraftSelectedColumns((prev) => {
        const isCurrentlyVisible = prev.includes(columnName);
        if (isCurrentlyVisible) {
          // Don't allow removing the last column
          if (prev.length <= 1) return prev;
          return prev.filter((name) => name !== columnName);
        }
        // Adding a previously-hidden column: append at the end of visible list
        return [...prev, columnName];
      });
    },
    []
  );

  // Reorder visible columns via drag
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setDraftSelectedColumns((prev) => {
        const oldIndex = prev.indexOf(active.id as string);
        const newIndex = prev.indexOf(over.id as string);
        if (oldIndex === -1 || newIndex === -1) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
      track('feature:te_columns_reorder', { surface: 'selector' });
    },
    [track]
  );

  // Select all columns in draft — preserve current visible order, append remaining in metadata order
  const handleSelectAll = useCallback(() => {
    setDraftSelectedColumns((prev) => {
      const inSet = new Set(prev);
      const additions = columns
        .filter((col) => !inSet.has(col.name))
        .map((col) => col.name);
      return [...prev, ...additions];
    });
  }, [columns]);

  // Deselect all except first column (in display order) in draft
  const handleClearAll = useCallback(() => {
    setDraftSelectedColumns((prev) => {
      if (prev.length === 0) return columns.length > 0 ? [columns[0].name] : [];
      return [prev[0]];
    });
  }, [columns]);

  // Apply draft changes
  const handleApply = useCallback(() => {
    const allColumnNames = columns.map((col) => col.name);
    // Use '*' only when the draft contains all columns IN metadata order — order matters.
    const isMetadataOrder =
      draftSelectedColumns.length === allColumnNames.length &&
      draftSelectedColumns.every((name, idx) => name === allColumnNames[idx]);

    if (isMetadataOrder) {
      onSelectedColumnsChange('*');
    } else {
      onSelectedColumnsChange(draftSelectedColumns);
    }
    setIsOpen(false);
  }, [draftSelectedColumns, columns, onSelectedColumnsChange]);

  // Cancel and close
  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < navOrderedColumns.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) {
            handleApply();
          } else if (selectedIndex >= 0 && selectedIndex < navOrderedColumns.length) {
            handleToggleColumn(navOrderedColumns[selectedIndex].name);
          }
          break;
        case ' ':
          e.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < navOrderedColumns.length) {
            handleToggleColumn(navOrderedColumns[selectedIndex].name);
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleCancel();
          break;
      }
    },
    [navOrderedColumns, selectedIndex, handleToggleColumn, handleApply, handleCancel]
  );

  // Counts for trigger label
  const visibleCount = currentSelectedArray.length;
  const totalCount = columns.length;
  const allVisible = selectedColumns === '*' || visibleCount === totalCount;

  // Draft counts
  const draftCount = draftSelectedColumns.length;
  const allSelectedInDraft = draftCount === totalCount;

  const visibleIds = useMemo(() => visibleDraftColumns.map((c) => c.name), [visibleDraftColumns]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs gap-1.5"
          disabled={disabled || columns.length === 0}
          data-testid="column-selector-button"
        >
          <Columns3 className="h-3.5 w-3.5" />
          <span>Columns</span>
          {!allVisible && (
            <span className="text-muted-foreground">
              ({visibleCount}/{totalCount})
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="flex flex-col gap-2">
          <div className="text-xs font-medium text-muted-foreground">
            Show/Hide Columns
          </div>

          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search columns..."
              className="h-7 pl-7 text-xs"
              autoFocus
              data-testid="column-search-input"
            />
          </div>

          {/* Select All / Clear All buttons */}
          <div className="flex items-center gap-2 border-b border-border pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSelectAll}
              className="h-6 px-2 text-xs flex-1"
              disabled={allSelectedInDraft}
              data-testid="select-all-columns-button"
            >
              <Check className="h-3 w-3 mr-1" />
              Select All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-6 px-2 text-xs flex-1"
              disabled={draftCount <= 1}
              data-testid="clear-all-columns-button"
            >
              Clear All
            </Button>
          </div>

          {/* Column list */}
          <div className="max-h-64 overflow-y-auto" ref={listRef} data-testid="column-list">
            {navOrderedColumns.length === 0 ? (
              <div className="text-xs text-muted-foreground py-2 text-center">
                No columns found
              </div>
            ) : (
              <>
                {filteredVisible.length > 0 && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={visibleIds} strategy={verticalListSortingStrategy}>
                      <div data-testid="column-list-visible">
                        {filteredVisible.map((col, index) => (
                          <SortableVisibleRow
                            key={col.name}
                            column={col}
                            index={index}
                            isInDraft={true}
                            isHighlighted={index === selectedIndex}
                            isOnlyVisible={draftCount === 1}
                            onToggle={handleToggleColumn}
                            onHover={setSelectedIndex}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}

                {filteredVisible.length > 0 && filteredHidden.length > 0 && (
                  <div className="my-1 flex items-center gap-2 px-1">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      Hidden ({filteredHidden.length})
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}

                {filteredHidden.length > 0 && (
                  <div data-testid="column-list-hidden">
                    {filteredHidden.map((col, hIndex) => {
                      const index = filteredVisible.length + hIndex;
                      return (
                        <HiddenRow
                          key={col.name}
                          column={col}
                          index={index}
                          isHighlighted={index === selectedIndex}
                          onToggle={handleToggleColumn}
                          onHover={setSelectedIndex}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Apply/Cancel buttons - always visible when open */}
          <div className="flex items-center gap-2 border-t border-border pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="h-7 px-3 text-xs flex-1"
              data-testid="column-selector-cancel"
            >
              <X className="h-3 w-3 mr-1" />
              Cancel
            </Button>
            <Button
              variant={hasChanges ? 'default' : 'secondary'}
              size="sm"
              onClick={handleApply}
              className="h-7 px-3 text-xs flex-1"
              data-testid="column-selector-apply"
            >
              <Check className="h-3 w-3 mr-1" />
              Apply
              {hasChanges && (
                <span className="ml-1 text-[10px]">
                  ({draftCount})
                </span>
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
